/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { first, map, switchMap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { AbstractObject, TenantId, uuid } from '@src/app/model/abstract-object';
import { AbstractHttpService } from '@src/app/shared/abstract-http.service';
import { User } from '@src/app/model/user';
import { Omc } from '@src/app/model/Omc';
import { AgentDataService } from '@src/app/modules/agent/agent-data.service';
import { OmcService } from '@src/app/modules/agent/omc.service';
import { NewRequestStepperComponent, NewRequestStepperData }
    from '@src/app/modules/crm/new-request-stepper/new-request-stepper.component';
import { ServiceRequest } from '@src/app/model/serviceRequest';
import { CurrentUserService } from '@src/app/modules/user/current-user.service';
import { DialogOptions, PickDialogComponent } from '@src/app/shared/dialogs/pick-dialog/pick-dialog.component';
import { MessageService } from '@src/app/shared/message.service';
import { FieldSet, LAYOUT_OPTIONS } from '@src/app/shared/form/field-set/field-set.component';
import { FormConfig } from '@src/app/shared/form/FormConfig';
import { FormTextComponent } from '@src/app/shared/form/form-text/form-text.component';

/** The tenancy a new request belongs to - the OMC, and the agency raising it on the OMC's behalf. */
export interface RequestContext {
    omcId: uuid;
    /** The OMC's own team - the request's teamId. */
    teamId: TenantId;
    agentTeamId: TenantId;
    omc: Omc;
}

/**
 * What a caller knows about the request it wants raised. Everything is optional: pass the OMC team
 * if the page already knows it, a parent if the request hangs off one, and nothing at all from the
 * new menu. Anything NewRequestStepperData accepts (supplier, unit, template, ...) is passed on.
 */
export type NewRequestData = Partial<NewRequestStepperData> & {
    /** The OMC's own team. Saves asking which OMC when the page already knows. */
    teamId?: TenantId;
};

/**
 * Raising a request, and the question that always comes first: which OMC is it for.
 *
 * In an agency you choose which OMC; inside an OMC there is nothing to ask, because the agency is
 * always that team's primary agent. The server derives the same answer from teamId when the request
 * is posted - what is resolved here exists so the form can show the OMC's name and default the
 * owner the way the server would.
 *
 * Asked before anything opens rather than as fields on the form: a work order's budget codes,
 * schedules and suppliers are all OMC-scoped, so nothing can be filled in until this is settled.
 * Only asked when there is a genuine choice - one OMC resolves silently.
 *
 * newRequest() is what pages call: it settles the OMC, checks the user may create at all, and opens
 * the stepper. Every entry point in the app goes through it, so the rules live here once.
 */
@Injectable({ providedIn: 'root' })
export class RequestContextService {
    private dialog = inject(MatDialog);
    private omcSvc = inject(OmcService);
    private agentDataSvc = inject(AgentDataService);
    private currentUserSvc = inject(CurrentUserService);
    private msgSvc = inject(MessageService);

    /**
     * Raise a request. Emits the created request, or null if the user backed out at the OMC
     * question or closed the stepper. Navigation afterwards is the caller's - a page that lists
     * requests goes to the new one, a panel on a request just refreshes itself.
     */
    newRequest(data: NewRequestData = {}): Observable<ServiceRequest | null> {
        if (!this.canCreate()) {
            this.msgSvc.showErrorMessage($localize`You do not have permission to raise a request`);
            return of(null);
        }
        return this.contextFor(data).pipe(switchMap(context =>
            context ? this.openStepper(data, context) : of(null)
        ));
    }

    /** Whether this user may raise a request at all - there is no reduced form to fall back to. */
    canCreate(): boolean {
        const user = this.currentUserSvc.currentUser;
        return !!user && User.getPathPrivilege(user, 'crm/requests').post;
    }

    /**
     * The OMC, from whatever the caller knew. A parent settles it outright; a page that named its
     * team saves the question; only the new menu with nothing to go on has to ask.
     */
    private contextFor(data: NewRequestData): Observable<RequestContext | null> {
        if (data.context) {
            return of(data.context);
        }
        const teamId = data.parent?.teamId ?? data.teamId;
        return teamId ? this.forOmcTeam(teamId) : this.resolve();
    }

    private openStepper(data: NewRequestData, context: RequestContext): Observable<ServiceRequest | null> {
        // teamId did its job in contextFor; the stepper works from the resolved context instead.
        const stepperData: NewRequestStepperData = { requestType: 'case', ...data, context };
        delete (stepperData as NewRequestData).teamId;
        return this.dialog.open(NewRequestStepperComponent, {
            data: stepperData,
            autoFocus: false,
            // The stepper's own stylesheet decides its width - 760px normally, full bleed on a
            // phone. MatDialog's default 80vw cap would otherwise win over the phone rule.
            maxWidth: '100vw',
        }).afterClosed().pipe(first(), map((created: ServiceRequest) => created ?? null));
    }

    /** Emits null if the user cancels, or if they have nothing to raise a request against. */
    resolve(): Observable<RequestContext | null> {
        return this.currentUserSvc.getCurrentUser().pipe(first(), switchMap((user: User) =>
            user.inAgency ? this.fromAgency() : this.forOmcTeam(user.current_team_id)
        ));
    }

    /**
     * The same answer for a record that already names its OMC - a unit, a contact - so there is
     * nothing to ask.
     */
    forOmcTeam(teamId: TenantId): Observable<RequestContext | null> {
        return this.agentDataSvc.getOmcFor(teamId).pipe(first(), map(omc => {
            if (!omc) {
                this.msgSvc.showErrorMessage($localize`No agency is set up to take requests for this OMC`);
                return null;
            }
            return this.contextForOmc(omc);
        }));
    }

    private fromAgency(): Observable<RequestContext | null> {
        return this.agentDataSvc.getOmcList().pipe(first(), switchMap(omcs => {
            if (omcs.length === 1) {
                return of(this.contextForOmc(omcs[0]));
            }
            return this.pick(this.omcConfig(), this.omcSvc, { items: omcs })
                .pipe(map((omc: Omc) => omc ? this.contextForOmc(omc) : null));
        }));
    }

    /**
     * The omcs row is the agency's own, so its teamId is that agency - but the work is filed against
     * whoever the OMC nominates as primary agent, so warn where those differ. An agent covering for
     * another agency, or one mid-handover, would otherwise have no way of knowing.
     */
    private contextForOmc(omc: Omc): RequestContext {
        const agentTeamId = omc.primaryAgentId ?? omc.teamId;
        const actingAs = this.currentUserSvc.currentUser?.inAgency?.teamId;
        if (actingAs && actingAs !== agentTeamId) {
            this.msgSvc.warn($localize`This will be raised for ${omc.name}'s managing agency, not yours`);
        }
        return { omcId: omc.id, teamId: omc.omcTeamId, agentTeamId, omc };
    }

    private pick(config: FormConfig, service: AbstractHttpService,
                 options: Partial<DialogOptions> = {}): Observable<AbstractObject> {
        return this.dialog.open(PickDialogComponent,
            { data: { config, service, showSearch: true, ...options } })
            .afterClosed().pipe(first());
    }

    private omcConfig(): FormConfig {
        return new FormConfig({
            title: $localize`Which OMC is this for?`,
            mode: 'list',
            fieldSet: new FieldSet({
                fields: [
                    FormTextComponent.make('Name', 'name'),
                    FormTextComponent.make('Address', 'address'),
                ],
                formLayout: LAYOUT_OPTIONS.singleCol,
            }),
        });
    }
}
